import { useEffect, useState } from 'react';

import { Spinner } from '@blueprintjs/core';
import { Button, Dialog, Divider } from '@opendoor/mosaic';

import { useSessionContext } from '../../contexts/SessionContext';
import { GetBeforeAndAfterPhotosForMaintenanceRequest } from '../../graphql/athena/types/GetBeforeAndAfterPhotosForMaintenanceRequest';
import { OdProtosWebData_MaintenanceRequest_State as MaintenanceRequestState } from '../../graphql/athena/types/globalTypes';
import { MaintenanceRequestFields } from '../../graphql/athena/types/MaintenanceRequestFields';
import { WebNoteFields } from '../../graphql/athena/types/WebNoteFields';
import { useMarkMaintenanceRequestAsNeedsReview } from '../../graphql/mutations/web/maintenance/useMarkMaintenanceRequestAsNeedsReview';
import { usePostToMaintenanceRequestMessageThread } from '../../graphql/mutations/web/maintenance/usePostToMaintenanceRequestMessageThread';
import useRemoveWebPhoto from '../../graphql/mutations/web/maintenance/useRemoveWebPhoto';
import useGetBeforeAndAfterPhotosForMaintenanceRequest from '../../graphql/queries/web/maintenance/useGetBeforeAndAfterPhotosForMaintenanceRequest';
import useGetMessageThreadForMaintenanceRequest from '../../graphql/queries/web/maintenance/useGetMessageThreadForMaintenanceRequest';
import { useUploadPhotos } from '../../hooks/useUploadPhotos';
import { CloseXIconUrl } from '../../icons/Icons';
import { prettifyAddress } from '../../utils/address';
import { prettifyDate, protoDateToDate } from '../../utils/date';
import { prettifyKind } from '../../utils/maintenance';
import HorizontalLayout from '../layouts/HorizontalLayout';
import VerticalLayout from '../layouts/VerticalLayout';
import MessageThread from '../messages/MessageThread';
import InlinePhotoViewer from '../photos/InlinePhotoViewer';
import Toaster from '../Toaster';
import MaintenanceRequestFeedback from './MaintenanceRequestFeedback';
import MaintenanceRequestRequirements from './MaintenanceRequestRequirements';

export interface MaintenanceRequestDetailModalProps {
  visibleRequest: [MaintenanceRequestFields | null, (req: MaintenanceRequestFields | null) => void];
  refetchMaintenanceRequests: () => void;
}

const MaintenanceRequestDetailModal = ({
  visibleRequest,
  refetchMaintenanceRequests,
}: MaintenanceRequestDetailModalProps) => {
  const { session } = useSessionContext();
  const [request, setRequest] = visibleRequest;
  const [photoUploading, setPhotoUploading] = useState<string | null>(null);
  const [removingPhoto, setRemovingPhoto] = useState<string | null>(null);

  const {
    data,
    loading: photosLoading,
    refetch: refetchPhotos,
    startPolling,
    stopPolling,
  } = useGetBeforeAndAfterPhotosForMaintenanceRequest({
    variables: { requestId: request ? request.id : '' },
    skip: !!!request,
  });
  const { data: messageThreadData, refetch: refetchMessageThread } =
    useGetMessageThreadForMaintenanceRequest({
      variables: { requestId: request ? request.id : '' },
      skip: !request,
    });
  const [postToMessageThread, postToMessageThreadResponse] =
    usePostToMaintenanceRequestMessageThread();
  const [markAsNeedsReview, markAsNeedsReviewResponse] = useMarkMaintenanceRequestAsNeedsReview();
  const [uploadPhotos] = useUploadPhotos({
    maintenanceRequestId: request ? request.id : '',
  });
  const [removePhoto] = useRemoveWebPhoto();

  const incompletePhotoDataKind = (
    data: GetBeforeAndAfterPhotosForMaintenanceRequest | undefined,
  ) => {
    if (!data) {
      return null;
    }
    const beforePhotos = data.webMaintenance.getBeforeAndAfterPhotos?.beforePhotos || [];
    const afterPhotos = data.webMaintenance.getBeforeAndAfterPhotos?.afterPhotos || [];

    const beforePhotosMissingData = beforePhotos.find((p) => !p.fullSizeUrl);
    if (!!beforePhotosMissingData) return 'before';

    const afterPhotosMissingData = afterPhotos.find((p) => !p.fullSizeUrl);
    if (!!afterPhotosMissingData) return 'after';

    return null;
  };
  useEffect(() => {
    const incompleteKind = incompletePhotoDataKind(data);
    if (!incompleteKind) {
      stopPolling();
      setPhotoUploading(null);
    } else {
      startPolling(3000);
      setPhotoUploading(incompleteKind);
    }
  }, [data, startPolling, stopPolling]);

  if (!request) {
    return null;
  }

  const messages: WebNoteFields[] =
    messageThreadData?.webMaintenance.getMessageThread?.messageThread?.notes || [];
  const currentUser = session?.human || null;
  const beforePhotos = data?.webMaintenance.getBeforeAndAfterPhotos?.beforePhotos || [];
  const afterPhotos = data?.webMaintenance.getBeforeAndAfterPhotos?.afterPhotos || [];
  const messageThreadId =
    messageThreadData?.webMaintenance.getMessageThread?.messageThread?.id || '';

  const prettyAddr = prettifyAddress(request.address);
  const scheduledOnDate = request.scheduledOn ? protoDateToDate(request.scheduledOn) : null;
  const scheduledOn = scheduledOnDate ? prettifyDate(scheduledOnDate) : null;
  const feedback = request.approvalFeedback || request.denialFeedback || '';

  let typeScheduledOnSubtextComponent: JSX.Element;
  if (scheduledOn) {
    typeScheduledOnSubtextComponent = (
      <span style={styles.typeScheduledOnText}>
        {prettifyKind(request)} - Scheduled for{' '}
        <span style={styles.scheduledOn}>{scheduledOn}</span>
      </span>
    );
  } else {
    typeScheduledOnSubtextComponent = (
      <span style={styles.typeScheduledOnText}>
        {prettifyKind(request)} - Could not find scheduled date
      </span>
    );
  }

  const onPhotoUpload = (files: any[], kind: string) => {
    setPhotoUploading(kind);
    uploadPhotos(files, kind).then(() => {
      refetchPhotos();
    });
  };

  const onPhotoRemove = (fileName: string, kind: string) => {
    setRemovingPhoto(kind);
    removePhoto({
      variables: {
        maintenanceRequestId: request.id,
        fileName: fileName,
        kind: kind,
      },
    })
      .then(() => {
        refetchPhotos().then(() => {
          setRemovingPhoto(null);
        });
      })
      .catch((e) => {
        Toaster.show({
          message: `There was an error removing the photo: ${e.message}`,
          intent: 'danger',
        });
        setRemovingPhoto(null);
      });
  };

  const handleMarkComplete = () => {
    markAsNeedsReview({
      variables: { maintenanceRequestId: request.id },
    })
      .then(() => {
        refetchMaintenanceRequests();
        setRequest(null);
      })
      .catch((e) => {
        setRequest(null);
        Toaster.show({
          message: `There was an error marking request as complete: ${e.message}`,
          intent: 'danger',
        });
      });
  };

  const getStateBadge = () => {
    if (markAsNeedsReviewResponse.loading) {
      return <Spinner />;
    }

    switch (request.state) {
      case MaintenanceRequestState.PENDING:
        return (
          <Button
            color="secondary"
            size="small"
            variant="outlined"
            data-testid="mark-as-complete-button"
            onClick={handleMarkComplete}
          >
            Mark as complete
          </Button>
        );
      case MaintenanceRequestState.MISSED:
        return (
          <Button
            color="secondary"
            size="small"
            variant="outlined"
            data-testid="mark-as-complete-button"
            onClick={handleMarkComplete}
          >
            Mark as complete
          </Button>
        );
      case MaintenanceRequestState.NEEDS_REVIEW:
        return (
          <div style={styles.completedAndApprovedBadge} data-testid="completed-badge">
            Completed
          </div>
        );
      case MaintenanceRequestState.APPROVED:
        return (
          <div style={styles.completedAndApprovedBadge} data-testid="approved-badge">
            Approved
          </div>
        );
      case MaintenanceRequestState.INVOICED:
        return (
          <div style={styles.completedAndApprovedBadge} data-testid="approved-badge">
            Approved
          </div>
        );
      case MaintenanceRequestState.DENIED:
        return (
          <HorizontalLayout>
            <span style={styles.deniedInstructions}>
              If you are at an Opendoor site and need immediate support, please email
              partnersupport@opendoor.com
            </span>
            <div style={styles.deniedBadge} data-testid="denied-badge">
              Denied
            </div>
          </HorizontalLayout>
        );
      default:
        return (
          <div style={styles.deniedBadge} data-testid="invalid-badge">
            Invalid
          </div>
        );
    }
  };

  const handleCloseModal = () => {
    setRequest(null);
    refetchMaintenanceRequests();
  };

  const showBeforeSpinner =
    (photoUploading && photoUploading === 'before') ||
    (removingPhoto && removingPhoto === 'before');
  const showAfterSpinner =
    (photoUploading && photoUploading === 'after') || (removingPhoto && removingPhoto === 'after');

  return (
    <Dialog
      data-testid="maintenance-request-detail-modal"
      open={!!request}
      onClose={handleCloseModal}
      fullWidth
      maxWidth={'lg'}
    >
      <VerticalLayout style={styles.modalContainer}>
        <HorizontalLayout
          data-testid="maintenance-request-detail-modal-header"
          style={styles.header}
        >
          <HorizontalLayout>
            <img
              src={CloseXIconUrl}
              alt=""
              onClick={handleCloseModal}
              style={styles.closeIcon}
              data-testid="close-button"
            />
            <VerticalLayout>
              <span style={styles.address}>{prettyAddr}</span>
              {typeScheduledOnSubtextComponent}
            </VerticalLayout>
          </HorizontalLayout>
          {getStateBadge()}
        </HorizontalLayout>
        <HorizontalLayout>
          <VerticalLayout style={styles.photosAndRequirements}>
            {photosLoading ? <Spinner /> : null}
            <HorizontalLayout style={styles.beforeAndAfterPhotos}>
              <div
                className="w-50%"
                data-testid="before-photo-viewer"
                style={{ marginRight: '1vw' }}
              >
                {showBeforeSpinner ? (
                  <div style={styles.photosLoadingSpinnerStyle}>
                    <Spinner />
                  </div>
                ) : (
                  <InlinePhotoViewer
                    photos={beforePhotos}
                    title={'Before'}
                    visibleThumbnailsCount={4}
                    allowEdit={!(!!photoUploading || !!removingPhoto)}
                    onUpload={onPhotoUpload}
                    onRemove={(fileName: string) => {
                      onPhotoRemove(fileName, 'before');
                    }}
                  />
                )}
              </div>
              <div className="w-50%" data-testid="after-photo-viewer">
                {showAfterSpinner ? (
                  <div style={styles.photosLoadingSpinnerStyle}>
                    <Spinner />
                  </div>
                ) : (
                  <InlinePhotoViewer
                    photos={afterPhotos}
                    title={'After'}
                    visibleThumbnailsCount={4}
                    allowEdit={!(!!photoUploading || !!removingPhoto)}
                    onUpload={onPhotoUpload}
                    onRemove={(fileName: string) => {
                      onPhotoRemove(fileName, 'after');
                    }}
                  />
                )}
              </div>
            </HorizontalLayout>
            <div style={styles.requirements}>
              <MaintenanceRequestRequirements request={request} />
            </div>
          </VerticalLayout>
          <VerticalLayout style={{ ...styles.feedbackAndMessages, overflowY: 'scroll' }}>
            <MaintenanceRequestFeedback feedback={feedback} />
            <Divider className="mv-6" />
            <MessageThread
              messages={messages}
              title="Messages with Opendoor"
              textboxPlaceholderText="Send message to Opendoor"
              allowEdit
              onSubmitMessage={(newMessage) => {
                postToMessageThread({
                  variables: {
                    messageThreadId: messageThreadId,
                    authorUserToken: currentUser ? currentUser.token : '',
                    content: newMessage,
                  },
                })
                  .then(() => {
                    refetchMessageThread();
                  })
                  .catch((e) => {
                    Toaster.show({
                      message: `There was an error posting to message thread: ${e.message}`,
                      intent: 'danger',
                    });
                  });
              }}
              submitMessageResponse={postToMessageThreadResponse}
            />
          </VerticalLayout>
        </HorizontalLayout>
      </VerticalLayout>
    </Dialog>
  );
};

export default MaintenanceRequestDetailModal;

const styles = {
  modalContainer: {
    minHeight: '60vh',
    height: '100%',
    width: '100%',
  },
  header: {
    justifyContent: 'space-between',
    padding: 20,
    borderBottom: 1,
    borderStyle: 'solid',
    borderColor: '#EBEFF3',
  },
  closeIcon: {
    cursor: 'pointer',
    paddingRight: 20,
  },
  address: {
    fontWeight: '600',
    fontSize: 16,
  },
  typeScheduledOnText: {
    fontWeight: '400',
    fontSize: 14,
    color: '#6E6E6E',
    marginTop: 4,
  },
  scheduledOn: {
    fontWeight: '600',
    textDecoration: 'underline',
  },
  photosAndRequirements: {
    width: '75%',
    borderRight: 1,
    borderStyle: 'solid',
    borderColor: '#EBEFF3',
  },
  beforeAndAfterPhotos: {
    justifyContent: 'space-around',
    padding: '2vw',
  },
  feedbackAndMessages: {
    padding: '1vw',
    width: '25%',
    maxHeight: '80vh',
  },
  requirements: {
    padding: '2vw',
  },
  completedAndApprovedBadge: {
    backgroundColor: '#EAFFFB',
    borderRadius: 4,
    height: 40,
    width: 100,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    color: '#007F5E',
    fontWeight: 500,
    fontSize: 14,
  },
  deniedInstructions: {
    fontWeight: 500,
    fontSize: 14,
    maxWidth: 375,
    color: '#222222',
    marginRight: 10,
  },
  deniedBadge: {
    backgroundColor: '#FFF3F3',
    borderRadius: 4,
    height: 40,
    width: 100,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    color: '#C34143',
    fontWeight: 500,
    fontSize: 14,
  },
  photosLoadingSpinnerStyle: {
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100%',
    height: '100%',
  },
};
